<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import ReadMoreIndowStories from '$lib/PageSections/ReadMoreIndowStories/ReadMoreIndowStories.svelte';

	import image1 from './images/image1.jpg';
	import image2 from './images/image2.jpg';
	import image3 from './images/image3.jpg';
	import image4 from './images/image4.jpg';
	import image5 from './images/image5.jpg';
</script>

<Section>
	<h2 class="text-left">
		Preservation Meets Performance: <span class="text-indow-grey-dark"
			>Indow Inserts in Frank Lloyd Wright Homes</span
		>
	</h2>
	<p class="text-left">
		Frank Lloyd Wright's architectural vision hinges on organic design, with his
		characteristic windows, whether the patterned leaded glass or the
		groundbreaking mitered corner windows, serving as essential light screens
		that dissolve the boundary between the interior and the natural world.
	</p>
	<p class="text-left">
		Though invaluable to architectural history, these original, single-pane
		windows present a trade-off, prioritizing aesthetic integrity over modern
		performance standards like superior energy efficiency, noise reduction, and
		draft elimination.
	</p>
	<p class="text-left">
		Indow window inserts provide a nondestructive, virtually invisible solution
		that <a href="/solutions/preserve-historic-windows"
			>honors the original design</a
		>
		while dramatically improving a
		<a href="/solutions/window-noise-reduction">home's performance</a>. This
		feat is demonstrated by their successful use in two distinct Frank Lloyd
		Wright masterpieces.
	</p>
	<h2>
		The Ross House: <span class="text-indow-grey-dark"
			>Honoring Leaded Glass</span
		>
	</h2>

	<Image src={image4} />

	<p class="text-left">
		The Ross House in Glencoe, Illinois, a 1915 Wright home meticulously
		restored by architect John Eifler, required a solution to insulate its
		classic, single-pane leaded glass windows.
	</p>
	<p class="text-left">
		Eifler needed a modern efficiency boost without resorting to exterior storm
		windows that would visually compromise the iconic aesthetic. The answer was
		the installation of fifteen Indow inserts.
	</p>

	<Image src={image2} />

	<h3 class="text-left font-medium">Key Preservation & Performance Gains:</h3>
	<ul class="">
		<li>
			<p>
				Non-Destructive Installation: Using the patented Compression Tubing, the
				inserts press securely into the existing window frames without the need
				for any damaging tracks or magnetic systems. This was a critical factor
				for preserving the original, reconditioned wood.
			</p>
		</li>
		<li>
			<p>
				Virtual Invisibility: The inserts proved so "pretty invisible" that the
				exterior window sash remained clearly visible.
			</p>
		</li>
		<li>
			<p>
				Draft Elimination: The house became "much less drafty," significantly
				reducing furnace cycling.
			</p>
		</li>
		<li>
			<p>
				Superior Acoustic Performance: The inserts' <a
					href="/solutions/window-noise-reduction"
					>superior acoustic performance</a
				> made the home quieter, even muting the noise from a nearby commuter train.
			</p>
		</li>
	</ul>

	<h2 class="text-left">Insulating Wright's Mitered Corner Innovation</h2>

	<div
		class="
    mb-6 flex flex-col gap-6
    sm:flex-row
  "
	>
		<Image src={image3} />
		<Image src={image1} />
	</div>

	<p class="text-left">
		Wright's mitered glass corner windows represent a pinnacle of his design
		philosophy, created to remove all obstruction and amplify the connection to
		the outdoors. However, this single pane corner configuration is
		exceptionally difficult to insulate effectively against drafts and
		condensation.
	</p>
	<p class="text-left">
		In one such home, the owners sought to increase the windows' R-value without
		replacing the priceless glass. We worked with them to create a successful,
		customized installation method for fitting our inserts into this unique
		application.
	</p>

	<Image src={image5} class="pb-8" />

	<h2 class="text-left">The Technical Solution</h2>
	<p class="text-left">
		The key was creating an aluminum F-bar that could be secured at the corner.
		This F-bar provided the necessary fourth vertical stop for the two
		converging inserts to meet and achieve the required compression seal.
	</p>

	<h3 class="text-left font-medium">This innovation effectively:</h3>

	<ul>
		<li><p>Insulates the challenging mitered corner</p></li>
		<li><p>Eliminates condensation</p></li>
		<li><p>Increases the comfort of the home</p></li>
	</ul>
	<p class="text-left">
		The inserts remain easily removable, ensuring the original, invisible corner
		aesthetic can be instantly restored during warmer months. For a deeper look
		at this process, read the <a
			target="_blank"
			href="https://savewright.org/insulating-wrights-mitered-corner-windows/"
			>original Save Wright article</a
		>.
	</p>

	<h2 class="text-left">The Preservation Solution of Choice</h2>
	<p class="text-left">
		For owners of architecturally significant properties, our window inserts
		offer a clear path to modern performance standards. As demonstrated in these
		two remarkable Frank Lloyd Wright houses, we deliver superior energy
		efficiency, draft elimination, and noise reduction while remaining virtually
		invisible and entirely non-destructive. This commitment to preserving the
		historic integrity of the window, including the original sash and glass,
		establishes our product as the premier solution for <a
			href="/solutions/preserve-historic-windows"
			>historic and landmark buildings</a
		>.
	</p>
</Section>
<hr />
<ReadMoreIndowStories />
<GetAFreeEstimate />
